/* eslint-disable max-lines */
import type { ApiTitle } from "@lumi/data";
import type { Variants } from "framer-motion";
import { motion } from "framer-motion";
import { ArrowLeft, Search as SearchIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { EmptyState, Pagination } from "../../shared/components";
import { SPACING } from "../../shared/constants/spacing";
import { useTransitionNavigation } from "../../shared/hooks/useTransitionNavigation";
import { useTelemetry } from "../../telemetry/TelemetryContext";
import { SearchResultCard } from "./components/SearchResultCard";
import { useSearch, type SearchPagination, type SearchResult } from "./hooks/useSearch";

/**
 * Convert SearchResult to ApiTitle format for TitleCard compatibility
 */
function searchResultToApiTitle(result: SearchResult): ApiTitle {
  return {
    id: result.id,
    slug: result.slug,
    name: result.name,
    synopsis: result.synopsis,
    year: result.year,
    posterUrl: result.posterUrlHd,
    posterUrlHd: result.posterUrlHd,
    backdropUrlHd: result.backdropUrlHd,
    horizontalTileUrl: result.horizontalTileUrl,
    verticalTileUrl: result.verticalTileUrl,
    genres: result.genres,
    directorName: result.directorName,
    voteAverage: result.voteAverage,
    runtimeMinutes: result.runtimeMinutes,
    // Optional fields that may not be in search results
    description: null,
    durationSec: null,
    heroUrl: null,
    createdAt: null,
    tmdbId: null,
    country: null,
    castNames: null,
    castData: null,
    crewData: null,
    productionCompanies: null,
    logoUrl: null,
    logoSource: null,
    languages: null,
    resolution: null,
  };
}

/**
 * Loading skeleton for search results grid
 */
function SearchResultsSkeleton() {
  // Generate unique IDs for skeleton items to avoid array index keys
  const skeletonIds = Array.from({ length: 20 }, (_, i) => `skeleton-${Date.now()}-${i}`);

  return (
    <div className="grid justify-center gap-8" style={{ gridTemplateColumns: "repeat(auto-fit, 500px)" }}>
      {skeletonIds.map((id) => (
        <div key={id} className="w-full aspect-video bg-neutral-900 rounded-lg animate-pulse" />
      ))}
    </div>
  );
}

/**
 * Empty state when no search results found
 */
interface SearchEmptyStateProps {
  readonly query: string;
  readonly onBrowse: () => void;
}

function SearchEmptyState({ query, onBrowse }: SearchEmptyStateProps) {
  return (
    <EmptyState
      icon={<SearchIcon className="w-16 h-16" />}
      title="No results found"
      message={`We couldn't find any titles matching "${query}". Try adjusting your search or browse our collections.`}
      action={{
        label: "Browse Content",
        onClick: onBrowse,
      }}
    />
  );
}

/**
 * Search header with back button and search input
 */
interface SearchHeaderProps {
  readonly localQuery: string;
  readonly onQueryChange: (value: string) => void;
  readonly onSearch: (e: React.FormEvent) => void;
  readonly onBack: () => void;
  readonly autocompleteSuggestion?: string;
  readonly onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
}

function SearchHeader({
  localQuery,
  onQueryChange,
  onSearch,
  onBack,
  autocompleteSuggestion,
  onKeyDown,
}: SearchHeaderProps) {
  // Calculate the ghost text to show
  const ghostText =
    autocompleteSuggestion && localQuery.length > 0 ? autocompleteSuggestion.slice(localQuery.length) : "";

  return (
    <div className="sticky top-16 z-40 bg-black/95 backdrop-blur-sm border-b border-neutral-900">
      <div className="px-4 sm:px-6 lg:px-8 py-4 max-w-[1650px] mx-auto">
        <div className="flex items-center gap-4">
          <button
            onClick={onBack}
            className="p-2 hover:bg-neutral-900 rounded-lg transition-colors"
            aria-label="Go back"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>

          <form onSubmit={onSearch} className="flex-1">
            <div className="relative">
              <SearchIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-400 z-10" />

              {/* Ghost text overlay */}
              {ghostText && (
                <div className="absolute left-0 top-1/2 -translate-y-1/2 pl-12 pointer-events-none text-base text-neutral-600">
                  <span className="invisible">{localQuery}</span>
                  <span>{ghostText}</span>
                </div>
              )}

              <input
                type="text"
                value={localQuery}
                onChange={(e) => onQueryChange(e.target.value)}
                onKeyDown={onKeyDown}
                placeholder="Search titles..."
                className={`w-full pl-12 pr-4 ${SPACING.search.inputPadding} bg-transparent border border-neutral-800 rounded-lg text-white placeholder-neutral-500 focus:outline-none focus:border-lumi-accent relative z-10`}
              />
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

/**
 * Search content area with results
 */
interface SearchContentProps {
  readonly loading: boolean;
  readonly query: string;
  readonly results: SearchResult[];
  readonly pagination: SearchPagination | null;
  readonly currentPage: number;
  readonly totalPages: number;
  readonly onPageChange: (page: number) => void;
  readonly onBrowseContent: () => void;
  readonly transitionTo: (path: string, variant?: Variants) => void;
  readonly onContentClick: (action: string, contentId: string, metadata?: Record<string, unknown>) => void;
}

function SearchContent({
  loading,
  query,
  results,
  pagination,
  currentPage,
  totalPages,
  onPageChange,
  onBrowseContent,
  transitionTo,
  onContentClick,
}: SearchContentProps) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
      className="px-4 sm:px-6 lg:px-8 pt-8 max-w-[1650px] mx-auto"
    >
      {!loading && query && (
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-white mb-2">Search Results</h1>
          <p className="text-neutral-400">
            {pagination?.total ?? 0} result{(pagination?.total ?? 0) === 1 ? "" : "s"} for "
            <span className="text-white">{query}</span>"
          </p>
        </div>
      )}

      {loading && <SearchResultsSkeleton />}
      {!loading && query && results.length === 0 && <SearchEmptyState query={query} onBrowse={onBrowseContent} />}
      {!loading && results.length > 0 && (
        <>
          {/* Responsive grid with fixed-width cards for consistent gaps */}
          <div className="grid justify-center gap-8" style={{ gridTemplateColumns: "repeat(auto-fit, 500px)" }}>
            {results.map((result, index) => (
              <SearchResultCard
                key={result.id}
                result={result}
                title={searchResultToApiTitle(result)}
                index={index}
                transitionTo={transitionTo}
                onContentClick={onContentClick}
              />
            ))}
          </div>
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            hasMore={pagination?.hasMore ?? false}
            onPageChange={onPageChange}
          />
        </>
      )}
      {!loading && !query && (
        <EmptyState
          icon={<SearchIcon className="w-16 h-16" />}
          title="Start searching"
          message="Enter a search term to find titles in our collection."
        />
      )}
    </motion.div>
  );
}

/**
 * SearchResults Page
 * Full-page search results with grid layout, pagination, and URL query params
 */
export function SearchResults() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { transitionTo } = useTransitionNavigation();
  const { trackSearch, trackContentInteraction } = useTelemetry();
  const query = searchParams.get("q") || "";
  const pageParam = searchParams.get("page");
  const currentPage = pageParam ? Number.parseInt(pageParam, 10) : 1;

  const [localQuery, setLocalQuery] = useState(query);
  const { results, pagination, loading, search, suggestions, getSuggestions } = useSearch({
    debounceMs: 500, // Increased from 300ms to reduce jumpiness
    suggestionsDebounceMs: 250, // Increased from 150ms to reduce jumpiness
    limit: 20,
    immediate: true,
  });

  // Execute search when query or page changes
  useEffect(() => {
    if (query) {
      search(query);
      // Scroll to top on page change
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [query, currentPage, search]);

  // Track search results when pagination data is available
  useEffect(() => {
    if (query && pagination) {
      trackSearch(query, pagination.total, {
        page: currentPage,
        hasResults: pagination.total > 0,
        isFirstPage: currentPage === 1,
      });
    }
  }, [query, pagination, currentPage, trackSearch]);

  // Get suggestions as user types (for autocomplete)
  useEffect(() => {
    if (localQuery.trim()) {
      getSuggestions(localQuery.trim());
    }
  }, [localQuery, getSuggestions]);

  // Get the top title suggestion for inline autocomplete
  const autocompleteSuggestion = localQuery.trim()
    ? suggestions.find((s) => s.type === "title" && s.value.toLowerCase().startsWith(localQuery.toLowerCase()))?.value
    : undefined;

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (localQuery.trim()) {
      setSearchParams({ q: localQuery.trim(), page: "1" });
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Right arrow accepts autocomplete suggestion
    if (e.key === "ArrowRight" && autocompleteSuggestion && localQuery.length > 0) {
      const cursorAtEnd = e.currentTarget.selectionStart === localQuery.length;
      if (cursorAtEnd) {
        e.preventDefault();
        setLocalQuery(autocompleteSuggestion);
      }
    }
  };

  const handlePageChange = (page: number) => {
    setSearchParams({ q: query, page: String(page) });
  };

  const handleBack = () => {
    navigate(-1);
  };

  const totalPages = pagination ? Math.ceil(pagination.total / pagination.limit) : 0;

  const handleBrowseContent = () => {
    transitionTo("/home/browse");
  };

  return (
    <div className="min-h-screen bg-black text-white pb-20">
      <SearchHeader
        localQuery={localQuery}
        onQueryChange={setLocalQuery}
        onSearch={handleSearch}
        onBack={handleBack}
        autocompleteSuggestion={autocompleteSuggestion}
        onKeyDown={handleKeyDown}
      />

      <SearchContent
        loading={loading}
        query={query}
        results={results}
        pagination={pagination}
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={handlePageChange}
        onBrowseContent={handleBrowseContent}
        transitionTo={transitionTo}
        onContentClick={trackContentInteraction}
      />
    </div>
  );
}
